import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Layers, Plus, Pencil, X, Loader2, ChevronDown, XCircle,
} from 'lucide-react';

import {
    fetchCCTypes, fetchItCodesLookup, fetchTypeOfTaxes, fetchNatureOfTaxes, fetchDcasForSubDca,
    fetchApprovalStatusDCA, fetchApprovedDcaById, fetchDCAGrid,
    submitNewDCA, submitUpdateDCA, submitNewSubDCA,
    clearSaveResult, clearUpdateResult, clearSaveSubDCAResult, clearApprovedDca, resetDcaCreation,
    selectDcaCreation,
} from '../../slices/accountsSlice/dcaCreationSlice';

// ── Shared primitives (matches AssignCCandDCABudget / AddNewITCode conventions) ─
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';
const readonlyCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children, type = 'button' }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm' };
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700',
        gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const ChipGroup = ({ options, value, onChange }) => (
    <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                    value === opt.value
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-700'
                }`}>
                {opt.label}
            </button>
        ))}
    </div>
);

const CCTypeChecklist = ({ ccTypes, loading, selected, onChange, disabled }) => (
    <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-40 overflow-y-auto">
        {loading ? (
            <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-indigo-500" /></div>
        ) : ccTypes.length === 0 ? (
            <p className="text-xs text-gray-400">No cost centre types found.</p>
        ) : (
            <div className="flex flex-wrap gap-x-5 gap-y-2">
                {ccTypes.map((t) => (
                    <label key={t.CCTypeID} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={selected.includes(t.CCTypeID)}
                            disabled={disabled}
                            onChange={() => onChange(selected.includes(t.CCTypeID) ? selected.filter((id) => id !== t.CCTypeID) : [...selected, t.CCTypeID])}
                        />
                        {t.CCTypeDescription}
                    </label>
                ))}
            </div>
        )}
    </div>
);

const statusBadge = (status) => {
    if (status === 'Approved') return <Badge color="green">Approved</Badge>;
    if (status === 'Rejected') return <Badge color="red">Rejected</Badge>;
    return <Badge color="amber">Pending</Badge>;
};

// ═════════════════════════════════════════════════════════════════════════════
// GENERAL DCA FORM (Add + Edit)
// ═════════════════════════════════════════════════════════════════════════════

const GeneralDCAForm = ({ editRow, onDone, currentUser, roleId, ccTypes, ccTypesLoading }) => {
    const dispatch = useDispatch();
    const { saveLoading, saveResult, saveError, updateLoading, updateResult, updateError, approvedDca, approvedDcaLoading } = useSelector(selectDcaCreation);
    const isEdit = !!editRow;

    const [dcaCode, setDcaCode] = useState('');
    const [dcaName, setDcaName] = useState('');
    const [ccTypeIds, setCcTypeIds] = useState([]);

    useEffect(() => {
        if (isEdit) dispatch(fetchApprovedDcaById({ DcaId: editRow.DCAID, Dcatypeid: 1 }));
        return () => { dispatch(clearApprovedDca()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isEdit, editRow?.DCAID]);

    useEffect(() => {
        if (isEdit && approvedDca) {
            setDcaCode(approvedDca.BasicDCAInfo?.DCACode || '');
            setDcaName(approvedDca.BasicDCAInfo?.DCAName || '');
            setCcTypeIds(approvedDca.CCTypes || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [approvedDca]);

    useEffect(() => {
        if (saveResult) { toast.success('Account Head added successfully.'); dispatch(clearSaveResult()); onDone(); }
        if (saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    useEffect(() => {
        if (updateResult) { toast.success('Account Head updated successfully.'); dispatch(clearUpdateResult()); onDone(); }
        if (updateError) { toast.error(updateError); dispatch(clearUpdateResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateResult, updateError]);

    const busy = saveLoading || updateLoading;

    const handleSubmit = () => {
        if (!isEdit && !dcaCode.trim()) { toast.warning('Enter Account Head Code.'); return; }
        if (!dcaName.trim()) { toast.warning('Enter Account Head Name.'); return; }
        if (ccTypeIds.length === 0) { toast.warning('Select at least one Cost Centre / Job Centre Type.'); return; }

        if (isEdit) {
            dispatch(submitUpdateDCA({
                DCACode: dcaCode,
                DCAName: dcaName.trim(),
                DCATypeID: 1,
                TypeOfTax: 0,
                NatureOfTax: 0,
                ITCodeID: approvedDca?.BasicDCAInfo?.ITCodeID || 0,
                CCTypeIDs: ccTypeIds,
                TaxNoIDs: [],
                Createdby: currentUser,
                RoleId: parseInt(roleId, 10) || 0,
                CheckUpdationType: editRow.Status === 'Approved' ? 'Insert' : 'ReturnUpdate',
            }));
        } else {
            dispatch(submitNewDCA({
                DCACodeID: 0,
                DCACode: dcaCode.trim().toUpperCase(),
                DCAName: dcaName.trim(),
                DCATypeID: 1,
                TypeOfTax: 0,
                NatureOfTax: 0,
                ITCodeID: 0,
                CCTypeIDs: ccTypeIds,
                Createdby: currentUser,
                RoleId: parseInt(roleId, 10) || 0,
            }));
        }
    };

    if (isEdit && approvedDcaLoading) {
        return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>;
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <Label required>Account Head Code</Label>
                {isEdit ? (
                    <input className={readonlyCls} value={dcaCode} readOnly />
                ) : (
                    <input className={inputCls} value={dcaCode} onChange={(e) => setDcaCode(e.target.value)} disabled={busy} style={{ textTransform: 'uppercase' }} />
                )}
                {!isEdit && <p className="text-xs text-gray-400 mt-1">Saved as DCA-{dcaCode.trim().toUpperCase() || '…'}</p>}
            </div>
            <div>
                <Label required>Account Head Name</Label>
                <input className={inputCls} value={dcaName} onChange={(e) => setDcaName(e.target.value)} disabled={busy} />
            </div>
            <div className="md:col-span-2">
                <Label required>Cost Centre / Job Centre Type</Label>
                <CCTypeChecklist ccTypes={ccTypes} loading={ccTypesLoading} selected={ccTypeIds} onChange={setCcTypeIds} disabled={busy} />
            </div>
            <div className="md:col-span-2 flex justify-end">
                <Btn onClick={handleSubmit} loading={busy}>{isEdit ? 'Submit' : 'Add'}</Btn>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAX DCA FORM (Add + Edit)
// ═════════════════════════════════════════════════════════════════════════════

const TaxDCAForm = ({ editRow, onDone, currentUser, roleId, ccTypes, ccTypesLoading, typeOfTaxes, typeOfTaxesLoading, natureOfTaxes, natureOfTaxesLoading }) => {
    const dispatch = useDispatch();
    const { saveLoading, saveResult, saveError, updateLoading, updateResult, updateError, approvedDca, approvedDcaLoading } = useSelector(selectDcaCreation);
    const isEdit = !!editRow;

    const [dcaCode, setDcaCode] = useState('');
    const [dcaName, setDcaName] = useState('');
    const [typeOfTax, setTypeOfTax] = useState('');
    const [natureOfTax, setNatureOfTax] = useState('');
    const [ccTypeIds, setCcTypeIds] = useState([]);

    useEffect(() => {
        if (isEdit) dispatch(fetchApprovedDcaById({ DcaId: editRow.DCAID, Dcatypeid: 2 }));
        return () => { dispatch(clearApprovedDca()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isEdit, editRow?.DCAID]);

    useEffect(() => {
        if (isEdit && approvedDca) {
            setDcaCode(approvedDca.BasicDCAInfo?.DCACode || '');
            setDcaName(approvedDca.BasicDCAInfo?.DCAName || '');
            setTypeOfTax(String(approvedDca.BasicDCAInfo?.TypeOfTaxID || ''));
            setNatureOfTax(String(approvedDca.BasicDCAInfo?.NatureOfTaxID || ''));
            setCcTypeIds(approvedDca.CCTypes || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [approvedDca]);

    useEffect(() => {
        if (saveResult) { toast.success('Tax Account Head added successfully.'); dispatch(clearSaveResult()); onDone(); }
        if (saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    useEffect(() => {
        if (updateResult) { toast.success('Tax Account Head updated successfully.'); dispatch(clearUpdateResult()); onDone(); }
        if (updateError) { toast.error(updateError); dispatch(clearUpdateResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateResult, updateError]);

    const busy = saveLoading || updateLoading;

    const handleSubmit = () => {
        if (!isEdit && !dcaCode.trim()) { toast.warning('Enter Account Head Code.'); return; }
        if (!dcaName.trim()) { toast.warning('Enter Account Head Name.'); return; }
        if (!typeOfTax) { toast.warning('Select Type Of Tax.'); return; }
        if (!natureOfTax) { toast.warning('Select Nature Of Tax.'); return; }
        if (ccTypeIds.length === 0) { toast.warning('Select at least one Cost Centre / Job Centre Type.'); return; }

        if (isEdit) {
            dispatch(submitUpdateDCA({
                DCACode: dcaCode,
                DCAName: dcaName.trim(),
                DCATypeID: 2,
                TypeOfTax: parseInt(typeOfTax, 10),
                NatureOfTax: parseInt(natureOfTax, 10),
                ITCodeID: approvedDca?.BasicDCAInfo?.ITCodeID || 0,
                CCTypeIDs: ccTypeIds,
                TaxNoIDs: [],
                Createdby: currentUser,
                RoleId: parseInt(roleId, 10) || 0,
                CheckUpdationType: editRow.Status === 'Approved' ? 'Insert' : 'ReturnUpdate',
            }));
        } else {
            dispatch(submitNewDCA({
                DCACodeID: 0,
                DCACode: dcaCode.trim().toUpperCase(),
                DCAName: dcaName.trim(),
                DCATypeID: 2,
                TypeOfTax: parseInt(typeOfTax, 10),
                NatureOfTax: parseInt(natureOfTax, 10),
                ITCodeID: 0,
                CCTypeIDs: ccTypeIds,
                Createdby: currentUser,
                RoleId: parseInt(roleId, 10) || 0,
            }));
        }
    };

    if (isEdit && approvedDcaLoading) {
        return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>;
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <Label required>Type Of Tax</Label>
                <SelectWrap loading={typeOfTaxesLoading}>
                    <select className={selectCls} value={typeOfTax} onChange={(e) => setTypeOfTax(e.target.value)} disabled={busy}>
                        <option value="">-Please Select-</option>
                        {typeOfTaxes.map((t) => <option key={t.TypeOfTaxID} value={t.TypeOfTaxID}>{t.TypeOfTaxDescription}</option>)}
                    </select>
                </SelectWrap>
            </div>
            <div>
                <Label required>Nature Of Tax</Label>
                <SelectWrap loading={natureOfTaxesLoading}>
                    <select className={selectCls} value={natureOfTax} onChange={(e) => setNatureOfTax(e.target.value)} disabled={busy}>
                        <option value="">-Please Select-</option>
                        {natureOfTaxes.map((n) => <option key={n.NatureOfTaxID} value={n.NatureOfTaxID}>{n.NatureOfTaxDescription}</option>)}
                    </select>
                </SelectWrap>
            </div>
            <div>
                <Label required>Account Head Code</Label>
                {isEdit ? (
                    <input className={readonlyCls} value={dcaCode} readOnly />
                ) : (
                    <input className={inputCls} value={dcaCode} onChange={(e) => setDcaCode(e.target.value)} disabled={busy} style={{ textTransform: 'uppercase' }} />
                )}
                {!isEdit && <p className="text-xs text-gray-400 mt-1">Saved as DCA-{dcaCode.trim().toUpperCase() || '…'}</p>}
            </div>
            <div>
                <Label required>Account Head Name</Label>
                <input className={inputCls} value={dcaName} onChange={(e) => setDcaName(e.target.value)} disabled={busy} />
            </div>
            <div className="md:col-span-2">
                <Label required>Cost Centre / Job Centre Type</Label>
                <CCTypeChecklist ccTypes={ccTypes} loading={ccTypesLoading} selected={ccTypeIds} onChange={setCcTypeIds} disabled={busy} />
            </div>
            <div className="md:col-span-2 flex justify-end">
                <Btn onClick={handleSubmit} loading={busy}>{isEdit ? 'Submit' : 'Add'}</Btn>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SUB DCA FORM (Add only — editing existing Sub DCAs lives on a separate page)
// ═════════════════════════════════════════════════════════════════════════════

const SubDCAForm = ({ onDone, currentUser, roleId, dcasForSubDca, dcasForSubDcaLoading, itCodes, itCodesLoading }) => {
    const dispatch = useDispatch();
    const { saveSubDCALoading, saveSubDCAResult, saveSubDCAError } = useSelector(selectDcaCreation);

    const [parentDcaId, setParentDcaId] = useState('');
    const [subDcaCode, setSubDcaCode] = useState('');
    const [subDcaName, setSubDcaName] = useState('');
    const [itCodeId, setItCodeId] = useState('');

    useEffect(() => {
        if (saveSubDCAResult) { toast.success('Sub Account Head added successfully.'); dispatch(clearSaveSubDCAResult()); onDone(); }
        if (saveSubDCAError) { toast.error(saveSubDCAError); dispatch(clearSaveSubDCAResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveSubDCAResult, saveSubDCAError]);

    const handleSubmit = () => {
        if (!parentDcaId) { toast.warning('Select an Account Head.'); return; }
        if (!subDcaCode.trim()) { toast.warning('Enter Sub Account Head Code.'); return; }
        if (!subDcaName.trim()) { toast.warning('Enter Sub Account Head Name.'); return; }
        if (!itCodeId) { toast.warning('Select an IT Code.'); return; }

        dispatch(submitNewSubDCA({
            DCACodeID: parseInt(parentDcaId, 10),
            SubDCACode: subDcaCode.trim().toUpperCase(),
            SubDCAName: subDcaName.trim(),
            ITCodeID: parseInt(itCodeId, 10),
            Createdby: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
        }));
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <Label required>Account Head</Label>
                <SelectWrap loading={dcasForSubDcaLoading}>
                    <select className={selectCls} value={parentDcaId} onChange={(e) => setParentDcaId(e.target.value)} disabled={saveSubDCALoading}>
                        <option value="">-Please Select-</option>
                        {dcasForSubDca.map((d) => <option key={d.DCAID} value={d.DCAID}>{d.DCAIDSTR}</option>)}
                    </select>
                </SelectWrap>
            </div>
            <div>
                <Label required>Sub Account Head Code</Label>
                <input className={inputCls} value={subDcaCode} onChange={(e) => setSubDcaCode(e.target.value)} disabled={saveSubDCALoading} style={{ textTransform: 'uppercase' }} />
            </div>
            <div>
                <Label required>Sub Account Head Name</Label>
                <input className={inputCls} value={subDcaName} onChange={(e) => setSubDcaName(e.target.value)} disabled={saveSubDCALoading} />
            </div>
            <div>
                <Label required>IT Code</Label>
                <SelectWrap loading={itCodesLoading}>
                    <select className={selectCls} value={itCodeId} onChange={(e) => setItCodeId(e.target.value)} disabled={saveSubDCALoading}>
                        <option value="">-Please Select-</option>
                        {itCodes.map((i) => <option key={i.ITCodeID} value={i.ITCodeID}>{i.ITCodename}</option>)}
                    </select>
                </SelectWrap>
            </div>
            <div className="md:col-span-2 flex justify-end">
                <Btn onClick={handleSubmit} loading={saveSubDCALoading}>Add</Btn>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD ACCOUNT HEAD MODAL (type picker: General / Tax / Sub)
// ═════════════════════════════════════════════════════════════════════════════

const DCA_TYPE_OPTIONS = [
    { value: 'general', label: 'General DCA' },
    { value: 'tax', label: 'Tax DCA' },
    { value: 'sub', label: 'Sub DCA' },
];

const AddDCAModal = ({ onClose, currentUser, roleId, lookups }) => {
    const [dcaType, setDcaType] = useState('general');

    return (
        <Modal title="Add Account Head and Sub Account Head" onClose={onClose}>
            <div className="mb-4">
                <Label required>Account Head Type</Label>
                <ChipGroup options={DCA_TYPE_OPTIONS} value={dcaType} onChange={setDcaType} />
            </div>
            {dcaType === 'general' && (
                <GeneralDCAForm onDone={onClose} currentUser={currentUser} roleId={roleId} ccTypes={lookups.ccTypes} ccTypesLoading={lookups.ccTypesLoading} />
            )}
            {dcaType === 'tax' && (
                <TaxDCAForm
                    onDone={onClose} currentUser={currentUser} roleId={roleId}
                    ccTypes={lookups.ccTypes} ccTypesLoading={lookups.ccTypesLoading}
                    typeOfTaxes={lookups.typeOfTaxes} typeOfTaxesLoading={lookups.typeOfTaxesLoading}
                    natureOfTaxes={lookups.natureOfTaxes} natureOfTaxesLoading={lookups.natureOfTaxesLoading}
                />
            )}
            {dcaType === 'sub' && (
                <SubDCAForm
                    onDone={onClose} currentUser={currentUser} roleId={roleId}
                    dcasForSubDca={lookups.dcasForSubDca} dcasForSubDcaLoading={lookups.dcasForSubDcaLoading}
                    itCodes={lookups.itCodes} itCodesLoading={lookups.itCodesLoading}
                />
            )}
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// EDIT ACCOUNT HEAD MODAL (General or Tax DCA only)
// ═════════════════════════════════════════════════════════════════════════════

const EditDCAModal = ({ row, onClose, currentUser, roleId, lookups }) => (
    <Modal title={`Edit Account Head — ${row.DCACode}`} onClose={onClose}>
        {row.DCATypeID === 2 ? (
            <TaxDCAForm
                editRow={row} onDone={onClose} currentUser={currentUser} roleId={roleId}
                ccTypes={lookups.ccTypes} ccTypesLoading={lookups.ccTypesLoading}
                typeOfTaxes={lookups.typeOfTaxes} typeOfTaxesLoading={lookups.typeOfTaxesLoading}
                natureOfTaxes={lookups.natureOfTaxes} natureOfTaxesLoading={lookups.natureOfTaxesLoading}
            />
        ) : (
            <GeneralDCAForm editRow={row} onDone={onClose} currentUser={currentUser} roleId={roleId} ccTypes={lookups.ccTypes} ccTypesLoading={lookups.ccTypesLoading} />
        )}
    </Modal>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const DCACreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const {
        ccTypes, ccTypesLoading, itCodes, itCodesLoading, typeOfTaxes, typeOfTaxesLoading,
        natureOfTaxes, natureOfTaxesLoading, dcasForSubDca, dcasForSubDcaLoading,
        allowedActions, grid, gridLoading, gridError,
    } = useSelector(selectDcaCreation);

    const [showAdd, setShowAdd] = useState(false);
    const [editRow, setEditRow] = useState(null);

    useEffect(() => {
        dispatch(fetchCCTypes());
        dispatch(fetchItCodesLookup());
        dispatch(fetchTypeOfTaxes());
        dispatch(fetchNatureOfTaxes());
        dispatch(fetchDcasForSubDca());
        dispatch(fetchDCAGrid());
        dispatch(fetchApprovalStatusDCA(roleId));
        return () => { dispatch(resetDcaCreation()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const lookups = { ccTypes, ccTypesLoading, itCodes, itCodesLoading, typeOfTaxes, typeOfTaxesLoading, natureOfTaxes, natureOfTaxesLoading, dcasForSubDca, dcasForSubDcaLoading };

    const canAct = (info) => info.Status === 'Approved' && (!info.DCACloseStatus || info.DCACloseStatus === 'Rejected');

    const refreshGrid = () => {
        dispatch(fetchDCAGrid());
        dispatch(fetchDcasForSubDca());
    };

    const handleActionChange = (info, action) => {
        if (!action || action === 'Select' || action === '0') return;
        if (action === 'Edit') {
            if (info.DCATypeID === 1 || info.DCATypeID === 2) { setEditRow(info); return; }
            toast.info('Editing Sub Account Heads isn\'t available on this page.');
            return;
        }
        toast.info(`"${action}" isn't available on this page yet.`);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Layers className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'Account Head Creation'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Create Account Heads (General / Tax) and Sub Account Heads</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
                <Card>
                    <div className="p-4 flex flex-wrap items-center justify-end gap-3">
                        <Btn onClick={() => setShowAdd(true)}><Plus className="h-3.5 w-3.5" />New Account Head And Sub Account Head</Btn>
                    </div>
                </Card>

                <Card>
                    {gridLoading ? (
                        <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : grid.length === 0 ? (
                        <EmptyState message="No Account Heads found." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        {['Account Head Code', 'Account Head Name', 'Type', 'Status', ''].map((h) => (
                                            <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {grid.map((row) => {
                                        const info = row.BasicDCAInfo;
                                        return (
                                            <tr key={info.DCAID} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{info.DCACode}</td>
                                                <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{info.DCAName}</td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{info.DCATypeName}</td>
                                                <td className="px-3 py-2.5">{statusBadge(info.Status)}</td>
                                                <td className="px-3 py-2.5">
                                                    {canAct(info) ? (
                                                        allowedActions.length > 0 ? (
                                                            <SelectWrap>
                                                                <select
                                                                    className={selectCls + ' py-1 text-xs'}
                                                                    defaultValue=""
                                                                    onChange={(e) => { handleActionChange(info, e.target.value); e.target.value = ''; }}
                                                                >
                                                                    <option value="">-Select-</option>
                                                                    {allowedActions.map((a, i) => <option key={i} value={a.Value}>{a.Text}</option>)}
                                                                </select>
                                                            </SelectWrap>
                                                        ) : (
                                                            <button onClick={() => setEditRow(info)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Edit">
                                                                <Pencil className="h-3.5 w-3.5" />
                                                            </button>
                                                        )
                                                    ) : (
                                                        <span className="text-xs text-gray-300 dark:text-gray-600 inline-flex items-center gap-1">
                                                            <XCircle className="h-3 w-3" /> —
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {showAdd && (
                <AddDCAModal onClose={() => { setShowAdd(false); refreshGrid(); }} currentUser={currentUser} roleId={roleId} lookups={lookups} />
            )}
            {editRow && (
                <EditDCAModal row={editRow} onClose={() => { setEditRow(null); refreshGrid(); }} currentUser={currentUser} roleId={roleId} lookups={lookups} />
            )}
        </div>
    );
};

export default DCACreation;
